// PIN management API functions

import { apiFetch, refreshAccessToken, authHeaders, API_BASE } from '@/lib/http';
import { pinStatusResponseSchema, pinActionResponseSchema } from './schema';

export type { PinStatusResponse, PinActionResponse } from './schema';

export async function fetchPinStatus() {
  return apiFetch('/auth/pin/status', { schema: pinStatusResponseSchema });
}

export async function setPin(data: { currentPassword: string; pin: string }) {
  return apiFetch('/auth/pin', {
    method: 'POST',
    body: JSON.stringify(data),
    schema: pinActionResponseSchema,
  });
}

export async function changePin(data: { currentPin: string; newPin: string }) {
  return apiFetch('/auth/pin', {
    method: 'PUT',
    body: JSON.stringify(data),
    schema: pinActionResponseSchema,
  });
}

export async function disablePin(data: { currentPin: string }) {
  return apiFetch('/auth/pin', {
    method: 'DELETE',
    body: JSON.stringify(data),
    schema: pinActionResponseSchema,
  });
}

// Own `fetch` (not apiFetch) because verify needs custom 401 handling: a wrong
// PIN also returns 401, and must not trigger a token refresh. Auth-header
// injection is still shared via authHeaders() so we don't reimplement it.
async function rawPinVerify(pin: string): Promise<Response> {
  return fetch(`${API_BASE}/auth/pin/verify`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ pin }),
  });
}

export async function verifyPin(pin: string): Promise<void> {
  // Use raw fetch instead of apiFetch to avoid automatic 401 retry logic.
  // Wrong PIN returns 401 with code "INVALID_PIN" — we handle that as a
  // normal error. But a 401 from JWT middleware (expired token) should
  // trigger a token refresh and retry, not show "wrong PIN".
  const response = await rawPinVerify(pin);

  if (response.status === 401) {
    let data: { error?: string; code?: string } = {};
    try {
      data = await response.json();
    } catch {
      // Non-JSON response (e.g. plain "Unauthorized" from JWT middleware)
    }

    if (data.code === 'INVALID_PIN') {
      throw new Error(data.error || 'Invalid PIN');
    }

    // Token expired — refresh and retry once
    await refreshAccessToken();
    const retryResponse = await rawPinVerify(pin);

    if (!retryResponse.ok) {
      const retryData = await retryResponse.json();
      throw new Error(retryData.error || 'PIN verification failed');
    }
    return;
  }

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'PIN verification failed');
  }
}
